/**
 * Carousel (E20-1087, generalized in E20-1365)
 *
 * The shared `<bfx-carousel>` element: a heading row with prev/next arrow controls above a
 * horizontally scrolling row of cards. Navigation: arrow buttons (page by ~one viewport of
 * cards) plus mouse/pen drag-to-scroll on desktop, and native touch swipe + a
 * styled scrollbar on mobile. Arrow controls disable (25% opacity via CSS) at each
 * end and when the row already fits with no scrolling. Cards land flush to the
 * content-container edge (CSS scroll-snap + scroll-padding). Reduced-motion aware.
 *
 * Mirrors the Feature Carousel (E20-1071) control/disabled logic; this variant drops
 * the pagination dots and height-equalizing (cards top-align at differing
 * heights) and pages the whole card row.
 *
 * Layout-agnostic by design — it reads its parts from data attributes, never from a section's
 * BEM classes, so each section owns its own look. Consumers: Product Carousel (E20-1087) and
 * Article Carousel (E20-1365). Mark up as:
 *   <bfx-carousel>
 *     <button data-carousel-prev> / <button data-carousel-next>
 *     <ul data-carousel-track><li data-carousel-cell>…</li></ul>
 *   </bfx-carousel>
 */
class BfxCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.cards = Array.from(this.querySelectorAll('[data-carousel-cell]'));
    if (!this.track) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.ticking = false;
    this.isDown = false;

    this.prev?.addEventListener('click', () => this.page(-1), { signal });
    this.next?.addEventListener('click', () => this.page(1), { signal });

    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
    window.addEventListener('resize', () => this.updateControls(), { passive: true, signal });

    // Mouse / pen drag-to-scroll (touch uses native scrolling).
    this.track.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
    this.track.addEventListener('pointermove', (e) => this.onPointerMove(e), { signal });
    this.track.addEventListener('pointerup', (e) => this.onPointerUp(e), { signal });
    this.track.addEventListener('pointercancel', (e) => this.onPointerUp(e), { signal });
    // Suppress a card link click that's really the end of a drag.
    this.track.addEventListener(
      'click',
      (e) => {
        if (this.dragged) {
          e.preventDefault();
          e.stopPropagation();
          this.dragged = false;
        }
      },
      { capture: true, signal }
    );

    this.updateControls();
  }

  reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // The content-container inset (track's start padding) that cards align to.
  gutter() {
    return parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
  }

  // One card's width plus the row gap. Cards are uniform, so a single measurement
  // is enough — avoids a per-card layout read on every scroll tick.
  cardOuterWidth() {
    const card = this.cards[0];
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  cardsPerView() {
    return Math.max(1, Math.floor(this.track.clientWidth / this.cardOuterWidth()));
  }

  activeIndex() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (scrollLeft <= 1) return 0;
    if (scrollLeft >= scrollWidth - clientWidth - 1) return this.cards.length - 1;

    const cardOuter = this.cardOuterWidth();
    if (cardOuter <= 0) return 0;
    const index = Math.round(scrollLeft / cardOuter);
    return Math.min(this.cards.length - 1, Math.max(0, index));
  }

  page(direction) {
    // Desktop scrolls freely by ~one viewport (smooth); mobile snaps to a whole-card
    // boundary so cards land flush to the content container.
    if (window.matchMedia('(min-width: 769px)').matches) {
      this.track.scrollBy({
        left: this.track.clientWidth * 0.9 * direction,
        behavior: this.reduceMotion() ? 'auto' : 'smooth',
      });
      return;
    }
    const target = this.activeIndex() + this.cardsPerView() * direction;
    this.scrollToCard(Math.min(this.cards.length - 1, Math.max(0, target)));
  }

  // Aligns a card's start edge to the content container (matches the snap point).
  scrollToCard(index) {
    const card = this.cards[index];
    if (!card) return;
    const target =
      card.getBoundingClientRect().left -
      this.track.getBoundingClientRect().left +
      this.track.scrollLeft -
      this.gutter();
    this.track.scrollTo({ left: target, behavior: this.reduceMotion() ? 'auto' : 'smooth' });
  }

  onPointerDown(e) {
    this.dragged = false;
    if (e.pointerType === 'touch' || e.button !== 0) return;
    this.isDown = true;
    this.pointerId = e.pointerId;
    this.startX = e.clientX;
    this.startScroll = this.track.scrollLeft;
  }

  onPointerMove(e) {
    if (!this.isDown || e.pointerId !== this.pointerId) return;
    if (e.buttons === 0) {
      this.onPointerUp(e);
      return;
    }
    const dx = e.clientX - this.startX;
    if (!this.dragged) {
      if (Math.abs(dx) <= 5) return; // not a drag yet — let clicks through to links
      this.dragged = true;
      this.track.setPointerCapture(this.pointerId);
      this.track.classList.add('is-dragging');
    }
    this.track.scrollLeft = this.startScroll - dx;
  }

  onPointerUp(e) {
    if (!this.isDown || e.pointerId !== this.pointerId) return;
    this.isDown = false;
    this.track.classList.remove('is-dragging');
    try {
      this.track.releasePointerCapture(e.pointerId);
    } catch {
      // pointer may already be released or was never captured
    }
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.updateControls();
      this.ticking = false;
    });
  }

  updateControls() {
    if (!this.prev || !this.next) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // Nothing to page in a direction → disable (covers "all cards fit": both disabled).
    this.prev.disabled = scrollLeft <= 1;
    this.next.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }
}

if (!customElements.get('bfx-carousel')) {
  customElements.define('bfx-carousel', BfxCarousel);
}
